import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../services/api'; 
import { useTheme } from '../../ThemeContext'; 
import Topbar from '../../Topbar';
import ChallengePlatform from './ChallengePlatform';

const ALL_MOCK_TESTS = [
  {
    id: 1,
    company_name: "TechCorp Inc.",
    job_name: "Full Stack Development Challenge",
    description: "Build a task app with a React frontend and Node.js backend. Must support CRUD, auth, and real-time updates.",
    job_description: "We are looking for a Full Stack Developer to design, deploy, and maintain robust client-server architectures at TechCorp Inc. You will build secure web apps and implement real-time channels using WebSockets.",
    skills: [{ name: "React" }, { name: "Node.js" }, { name: "MongoDB" }],
    test_duration_min: 90,
    xp: "+500 XP",
    experience_level: "HARD"
  },
  {
    id: 2,
    company_name: "TechCorp Inc.",
    job_name: "Senior Frontend Developer",
    description: "Implement a high-performance custom hook 'useCachedFetch' to optimize heavy analytics query re-renders.",
    job_description: "We are looking for a seasoned Senior Frontend Engineer to join TechCorp's product team. You will build scalable, performant React applications that serve millions of users. The ideal candidate is deeply familiar with component architecture, state management, and the full modern JavaScript ecosystem.",
    skills: [{ name: "React" }, { name: "TypeScript" }, { name: "CSS" }, { name: "Node.js" }, { name: "System Design" }, { name: "Next.js" }],
    test_duration_min: 120,
    xp: "+500 XP",
    experience_level: "SENIOR"
  },
  {
    id: 3,
    company_name: "CloudScale",
    job_name: "Cloud Architecture Design",
    description: "Design a scalable architecture for a web platform handling 1M+ daily requests using AWS services.",
    job_description: "We are looking for a Cloud Architect to join CloudScale's infrastructure squad. You will build resilient server layouts, optimize load balancers, and manage scalable containerized apps.",
    skills: [{ name: "AWS" }, { name: "Docker" }, { name: "Architecture" }],
    test_duration_min: 75,
    xp: "+450 XP",
    experience_level: "HARD"
  },
  {
    id: 4,
    company_name: "Finova Labs",
    job_name: "API Integration Challenge",
    description: "Build and document a public REST API returning structured JSON data with authentication and rate limiting.",
    job_description: "We are seeking a Backend Developer with extensive knowledge in REST APIs, authentication layers, JSON schemas, rate limiters, and clean API design methodologies.",
    skills: [{ name: "REST API" }, { name: "JavaScript" }, { name: "JSON" }],
    test_duration_min: 45,
    xp: "+280 XP",
    experience_level: "MID"
  },
  {
    id: 5,
    company_name: "SecureOps",
    job_name: "Cybersecurity Vulnerability Challenge",
    description: "Identify XSS and SQL injection vulnerabilities in a login system and implement fixes using proper session handling.",
    job_description: "We are seeking a Cybersecurity Analyst to perform rigorous security code reviews, secure active production systems, and prevent vulnerabilities including XSS, SQLi, and path traversals.",
    skills: [{ name: "Security" }, { name: "Authentication" }, { name: "Node" }],
    test_duration_min: 80,
    xp: "+520 XP",
    experience_level: "HARD"
  },
  {
    id: 6,
    company_name: "PlayForge Studios",
    job_name: "Game Logic Development Challenge",
    description: "Implement the core game logic for a small browser game such as Snake or Tic-Tac-Toe.",
    job_description: "We are seeking an interactive Game Developer to architect engaging and responsive web canvas visual interactions, input processors, and optimized physics frames.",
    skills: [{ name: "JavaScript" }, { name: "Logic" }, { name: "Canvas" }],
    test_duration_min: 60,
    xp: "+280 XP",
    experience_level: "MID"
  },
  {
    id: 7,
    company_name: "Platform",
    job_name: "Authentication Implementation Challenge",
    description: "Implement a secure authentication system using JWT tokens. Build register, login, and protected resource endpoints.",
    job_description: "Join us as a Security Middleware Engineer. You will design bulletproof authentication flows, configure secure cookie tokens, and build backend route guards.",
    skills: [{ name: "Security" }, { name: "Authentication" }, { name: "JWT" }],
    test_duration_min: 50,
    xp: "+240 XP",
    experience_level: "MID"
  },
  {
    id: 8,
    company_name: "Platform",
    job_name: "Git Workflow Challenge",
    description: "Demonstrate proper git branching strategies, commit conventions, and maintaining a clean commit history.",
    job_description: "We are searching for a DevOps Specialist to streamline release operations, orchestrate git branching pipelines, resolve conflicts, and direct robust CI/CD integration flows.",
    skills: [{ name: "Git" }, { name: "Version Control" }],
    test_duration_min: 30,
    xp: "+120 XP",
    experience_level: "EASY"
  },
  {
    id: 9,
    company_name: "Platform",
    job_name: "Database Query Challenge",
    description: "Write complex SQL queries to extract useful information from a database: joins, groupings, and complex filters.",
    job_description: "We are seeking a Database Administrator to optimize postgres query plans, write optimized analytics views, and configure indexing schemas.",
    skills: [{ name: "PostgreSQL" }, { name: "Queries" }, { name: "SQL" }],
    test_duration_min: 45,
    xp: "+200 XP",
    experience_level: "MID"
  },
  {
    id: 10,
    company_name: "Platform",
    job_name: "Backend API Development",
    description: "Build a REST API with endpoints for managing resources, including full validation, error handling, and basic auth.",
    job_description: "Join us as a Backend Engineer to create structured REST services, configure custom error boundaries, build validators, and write reliable automated tests.",
    skills: [{ name: "Node.js" }, { name: "REST API" }, { name: "Backend" }],
    test_duration_min: 70,
    xp: "+380 XP",
    experience_level: "HARD"
  },
  {
    id: 11,
    company_name: "Platform",
    job_name: "Frontend UI Challenge",
    description: "Recreate a modern landing page using HTML and CSS. The result must be responsive and adapts across desktop and mobile screens.",
    job_description: "We are seeking a Creative Frontend Designer to draft pixel-perfect layouts, write custom responsive CSS classes, and comply with strict web accessibility rules.",
    skills: [{ name: "Responsive" }, { name: "HTML" }, { name: "CSS" }],
    test_duration_min: 40,
    xp: "+220 XP",
    experience_level: "MID"
  },
  {
    id: 12,
    company_name: "Platform",
    job_name: "System Design Fundamentals Challenge",
    description: "Design the architecture for a scalable web application, including data structures, caching plans, and infrastructure.",
    job_description: "Join us as a Principal Architect to construct microservice topologies, configure horizontal scaling limits, design cache topologies, and plan storage layers.",
    skills: [{ name: "Architecture" }, { name: "Scalability" }, { name: "Design" }],
    test_duration_min: 80,
    xp: "+450 XP",
    experience_level: "HARD"
  }
];

export default function TestDetail() {
  const { pk } = useParams();
  const testId = pk;
  const navigate = useNavigate();
  const { C } = useTheme(); 

  const initialDetails = ALL_MOCK_TESTS.find(t => String(t.id) === String(testId)) || ALL_MOCK_TESTS[0];

  const [testDetails, setTestDetails] = useState(initialDetails);

  const [skills, setSkills] = useState(() => 
    (initialDetails.skills || []).map(s => typeof s === 'string' ? s : s.name)
  );

  const [flagInput, setFlagInput] = useState('');
  const [showChallenge, setShowChallenge] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const isFullStack = 
    testDetails?.job_name?.toLowerCase().includes('full stack') || 
    testDetails?.description?.toLowerCase().includes('node.js') || 
    String(testDetails?.id) === '1';

  const correctFlag = isFullStack ? 'FLAG{T3CH_C0RP_FS_EXP3RT_7721}' : 'FLAG{T3CH_FR0NT3ND_VIRTUAL_9918}';

  const handleFlagSubmit = async () => {
    const trimmedFlag = flagInput.trim();
    if (!trimmedFlag) {
      setSubmissionMessage({ type: 'error', text: '⚠️ Please enter a flag first.' });
      return;
    }

    if (trimmedFlag === correctFlag) {
      setSubmissionMessage(null);
      setShowSuccessModal(true);

      const testId = String(testDetails.id || 1);

      // ✅ 1 — Mark this test as completed so Tests.jsx disables the card
      const completed = JSON.parse(localStorage.getItem('completed_tests') || '[]');
      if (!completed.includes(testId)) {
        localStorage.setItem('completed_tests', JSON.stringify([...completed, testId]));
      }

      // ✅ 2 — Push a submission record so Dashboard shows it in Recent Submissions
      const localSubs = JSON.parse(localStorage.getItem('local_submissions') || '[]');
      const alreadyExists = localSubs.some(s => String(s.id) === testId);
      if (!alreadyExists) {
        const newSubmission = {
          id: testId,
          job_name: testDetails.job_name || 'Full Stack Development Challenge',
          company_name: testDetails.company_name || 'TechCorp Inc.',
          status: 'accepted',
          submitted_at: new Date().toISOString(),
          xp_earned: testDetails.xp || '+500 XP',
          score: 95,
          experience_level: testDetails.experience_level || 'Senior'
        };
        localStorage.setItem('local_submissions', JSON.stringify([newSubmission, ...localSubs]));
      }

      // ✅ 3 — POST to Django API to record XP and status in the real database
      try {
        await api.post(`/candidates/submissions/create/${testId}/`, {
          submitted_flag: trimmedFlag
        });
      } catch (err) {
        console.warn("Backend API database post failed (offline mode):", err);
      }

      setTimeout(() => {
        navigate('/dashboard/tests');
      }, 3500);
    } else {
      setSubmissionMessage({ type: 'error', text: '❌ Incorrect Flag. Please verify your solution in the platform.' });
    }
  };
  
  // Compteur initialisé à la durée du test (en secondes)
  const [timeLeft, setTimeLeft] = useState(() => (initialDetails.test_duration_min || 120) * 60);

  // useEffect pour récupérer les données du test
  useEffect(() => {
    const fetchTestData = async () => {
      // First update state from local fallback database
      const localMatch = ALL_MOCK_TESTS.find(t => String(t.id) === String(testId));
      if (localMatch) {
        setTestDetails(localMatch);
        setTimeLeft(localMatch.test_duration_min * 60);
        const mappedSkills = (localMatch.skills || []).map(s => {
          if (!s) return '';
          return typeof s === 'string' ? s : (s.name || '');
        }).filter(Boolean);
        setSkills(mappedSkills);
      }

      if (!testId) return;
      try {
        const response = await api.get('/candidates/saved-tests/', { timeout: 2500 });
        let remoteTests = [];
        if (response.data) {
          if (Array.isArray(response.data)) remoteTests = response.data;
          else if (Array.isArray(response.data.data)) remoteTests = response.data.data;
        }
        const currentTest = remoteTests.find(t => String(t.id) === String(testId));
        if (currentTest) {
          setTestDetails(currentTest);
          
          if (currentTest.test_duration_min) {
            setTimeLeft(currentTest.test_duration_min * 60);
          }

          const mappedSkills = (currentTest.skills || []).map(s => {
            if (!s) return '';
            return typeof s === 'string' ? s : (s.name || '');
          }).filter(Boolean);
          setSkills(mappedSkills);
        }
      } catch (err) {
        console.warn("Le serveur n'a pas répondu à temps, conservation des données locales.", err);
      }
    };
    fetchTestData();
  }, [testId]);

  // useEffect pour gérer le compte à rebours actif
  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);

    // Nettoyage de l'intervalle si le composant est démonté
    return () => clearInterval(timer);
  }, [timeLeft]);

  // Fonction pour formater les secondes restantes en HH:MM:SS
  const formatTime = (seconds) => {
    if (seconds <= 0) return "00:00:00";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return [
      hrs.toString().padStart(2, '0'),
      mins.toString().padStart(2, '0'),
      secs.toString().padStart(2, '0')
    ].join(':');
  };

  // On garde les variables master de ton thème qui s'adaptent bien
  const backgroundColor = C?.bgDeep || '#060814'; 
  const cardColor = C?.bgCard || '#0b0f19'; 
  const borderColor = C?.border || '#1e293b';
  const textColor = C?.textPrimary || '#ffffff'; 
  const textSecondary = C?.textSecond || '#64748b'; 

  // Style des inputs hérité et transparent (Zéro-bug de couleur)
  const inputStyle = {
    width: "100%",
    background: "rgba(0, 0, 0, 0.03)", 
    border: `1px solid ${borderColor}`,
    padding: "10px",
    borderRadius: "6px",
    color: "inherit", 
    fontSize: "13px",
    outline: "none",
    boxSizing: "border-box"
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100vh", overflowY: "auto", background: backgroundColor, color: textColor, fontFamily: 'sans-serif' }}>
      <Topbar title={testDetails?.job_name || "Test Details"} />

      <div style={{ padding: "24px", maxWidth: "1100px", margin: "0 auto", width: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: "24px" }}>
        
        {/* TITRE PRINCIPAL */}
        <div>
          <h1 style={{ fontSize: "24px", fontWeight: "700", marginBottom: "4px" }}>{testDetails?.job_name}</h1>
          <p style={{ fontSize: "12px", color: textSecondary }}>Read the full brief below, open the challenge platform, then submit your flag when done.</p>
        </div>

        {/* EN-TÊTE DU CHALLENGE & COMPTE À REBOURS INTEGRÉ */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: cardColor, border: `1px solid ${borderColor}`, borderRadius: "12px", padding: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div style={{ width: "44px", height: "44px", borderRadius: "8px", background: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "700", fontSize: "16px", color: "#fff" }}>TC</div>
            <div>
              <h2 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "4px" }}>{testDetails?.job_name || "Full Stack Development Challenge"}</h2>
              <div style={{ display: "flex", gap: "12px", fontSize: "12px", color: textSecondary }}>
                <span>🏢 {testDetails?.company_name || "TechCorp Inc."}</span>
                <span>⏱️ {testDetails?.test_duration_min || 120} min</span>
                <span style={{ color: "#f59e0b" }}>⭐ {testDetails?.xp || "+500 XP"}</span>
                <span style={{ background: "#ef4444", color: "#fff", padding: "1px 6px", borderRadius: "4px", fontSize: "10px", fontWeight: "700" }}>
                  {typeof testDetails?.experience_level === 'string' ? testDetails.experience_level.toUpperCase() : "SENIOR"}
                </span>
              </div>
            </div>
          </div>
          <div style={{ background: backgroundColor, border: `1px solid ${borderColor}`, padding: "10px 18px", borderRadius: "8px", textAlign: "right" }}>
            {/* ICI LE COMPTEUR AUTOMATIQUE ET DYNAMIQUE */}
            <div style={{ fontSize: "20px", fontWeight: "700", fontFamily: "monospace", color: timeLeft < 600 ? "#ef4444" : "#f87171" }}>
              {formatTime(timeLeft)}
            </div>
            <div style={{ fontSize: "10px", color: textSecondary, textTransform: "uppercase" }}>Time Remaining</div>
          </div>
        </div>

        {/* STEP 01: JOB INFORMATION */}
        <div style={{ background: cardColor, border: `1px solid ${borderColor}`, borderRadius: "12px", padding: "20px", display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <span style={{ fontSize: "10px", color: "#2563eb", fontWeight: "700", textTransform: "uppercase" }}>Step 01</span>
            <h3 style={{ fontSize: "14px", fontWeight: "600" }}>Job Information</h3>
            <p style={{ fontSize: "11px", color: textSecondary }}>This assessment is for the role described below. Read carefully before starting the challenge.</p>
          </div>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div>
              <label style={{ fontSize: "11px", color: textSecondary, display: "block", marginBottom: "6px" }}>Job Title</label>
              <input type="text" readOnly value={testDetails?.job_name || "Senior Frontend Engineer"} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: "11px", color: textSecondary, display: "block", marginBottom: "6px" }}>Department</label>
              <input type="text" readOnly value="Engineering" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: "11px", color: textSecondary, display: "block", marginBottom: "6px" }}>Experience Level</label>
              <input type="text" readOnly value={testDetails?.experience_level || "Senior"} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: "11px", color: textSecondary, display: "block", marginBottom: "6px" }}>Employment Type</label>
              <input type="text" readOnly value="Full-time" style={inputStyle} />
            </div>
          </div>

          <div>
            <label style={{ fontSize: "11px", color: textSecondary, display: "block", marginBottom: "6px" }}>Job Description</label>
            <div style={{ ...inputStyle, lineHeight: "1.5" }}>
              {testDetails?.job_description || testDetails?.description || "No job description provided."}
            </div>
          </div>
        </div>

        {/* STEP 02: SKILLS BEING EVALUATED */}
        <div style={{ background: cardColor, border: `1px solid ${borderColor}`, borderRadius: "12px", padding: "20px" }}>
          <span style={{ fontSize: "10px", color: "#2563eb", fontWeight: "700", textTransform: "uppercase" }}>Step 02</span>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "2px" }}>Skills Being Evaluated</h3>
          <p style={{ fontSize: "11px", color: textSecondary, marginBottom: "14px" }}>Your performance on the highlighted competencies will be scored and reported to TechCorp.</p>
          
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {skills.map((skill, idx) => (
              <span key={idx} style={{ background: "rgba(37, 99, 235, 0.1)", color: "#2563eb", border: "1px solid rgba(37, 99, 235, 0.25)", padding: "6px 14px", borderRadius: "6px", fontSize: "12px", fontWeight: "600" }}>
                {skill}
              </span>
            ))}
          </div>
        </div>

        {/* STEP 03: CHALLENGE DESIGN */}
        <div style={{ background: cardColor, border: `1px solid ${borderColor}`, borderRadius: "12px", padding: "20px", display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <span style={{ fontSize: "10px", color: "#2563eb", fontWeight: "700", textTransform: "uppercase" }}>Step 03</span>
            <h3 style={{ fontSize: "14px", fontWeight: "600" }}>Challenge Design</h3>
            <p style={{ fontSize: "11px", color: textSecondary }}>Read the challenge description and scoring criteria carefully before opening the external platform.</p>
          </div>

          <div>
            <label style={{ fontSize: "11px", color: textSecondary, display: "block", marginBottom: "6px" }}>Challenge Description</label>
            <div style={{ ...inputStyle, lineHeight: "1.6" }}>
              {testDetails?.description}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div>
              <label style={{ fontSize: "11px", color: textSecondary, display: "block", marginBottom: "6px" }}>Challenge Type</label>
              <input type="text" readOnly value="Coding Challenge" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: "11px", color: textSecondary, display: "block", marginBottom: "6px" }}>Difficulty</label>
              <div style={{ ...inputStyle, display: "flex", alignItems: "center" }}>
                <span style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444", padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: "700" }}>
                  {testDetails?.experience_level || "Senior"}
                </span>
              </div>
            </div>
          </div>

          <div>
            <label style={{ fontSize: "11px", color: textSecondary, display: "block", marginBottom: "6px" }}>Scoring Criteria</label>
            <input type="text" readOnly value="Code quality & structure 30%  ·  Performance & optimization 30%  ·  UX & design fidelity 20%  ·  Documentation & tests 20%" style={{ ...inputStyle, color: textSecondary, fontSize: "12px" }} />
          </div>
        </div>

        {/* STEP 04: TIME & ACCESS */}
        <div style={{ background: cardColor, border: `1px solid ${borderColor}`, borderRadius: "12px", padding: "20px", display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <span style={{ fontSize: "10px", color: "#2563eb", fontWeight: "700", textTransform: "uppercase" }}>Step 04</span>
            <h3 style={{ fontSize: "14px", fontWeight: "600" }}>Time & Access</h3>
            <p style={{ fontSize: "11px", color: textSecondary }}>Challenge window and access details. Click "Open Challenge" to begin on the external platform.</p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div>
              <label style={{ fontSize: "11px", color: textSecondary, display: "block", marginBottom: "6px" }}>Time Limit</label>
              <input type="text" readOnly value="2 hours" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: "11px", color: textSecondary, display: "block", marginBottom: "6px" }}>Max Candidates</label>
              <input type="text" readOnly value="500" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: "11px", color: textSecondary, display: "block", marginBottom: "6px" }}>Opens</label>
              <input type="text" readOnly value="2026-05-21" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: "11px", color: textSecondary, display: "block", marginBottom: "6px" }}>Closes</label>
              <input type="text" readOnly value="2026-06-30" style={inputStyle} />
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: backgroundColor, border: `1px solid ${borderColor}`, padding: "12px", borderRadius: "8px", marginTop: "8px" }}>
            <span style={{ fontSize: "13px", color: "#2563eb", fontFamily: "monospace", wordBreak: 'break-all' }}>platform.io/challenge/techcorp-senior-frontend-2026</span>
            <button 
              onClick={() => setShowChallenge(true)}
              style={{ background: "#2563eb", color: "#fff", border: "none", padding: "8px 16px", borderRadius: "6px", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}
            >
              Open Challenge
            </button>
          </div>
        </div>

        {/* SUBMIT YOUR FLAG */}
        <div style={{ background: cardColor, border: `1px solid ${borderColor}`, borderRadius: "12px", padding: "20px" }}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "4px", display: "flex", alignItems: "center", gap: "8px" }}>💬 Submit Your Flag</h3>
          <p style={{ fontSize: "11px", color: textSecondary, marginBottom: "16px" }}>After completing the challenge on the external platform, enter the Flag it provides to record your result.</p>
          
          {submissionMessage && (
            <div style={{
              background: submissionMessage.type === 'error' ? "rgba(239, 68, 68, 0.1)" : "rgba(16, 185, 129, 0.1)",
              border: `1px solid ${submissionMessage.type === 'error' ? '#ef4444' : '#10b981'}`,
              color: submissionMessage.type === 'error' ? '#f87171' : '#34d399',
              padding: "10px 14px",
              borderRadius: "6px",
              fontSize: "12px",
              marginBottom: "14px",
              fontWeight: "600"
            }}>
              {submissionMessage.text}
            </div>
          )}

          <div style={{ display: "flex", gap: "12px" }}>
            <input 
              type="text" 
              placeholder="FLAG{your_challenge_flag_here}" 
              value={flagInput}
              onChange={(e) => setFlagInput(e.target.value)}
              style={{ ...inputStyle, background: "transparent" }} 
            />
            <button 
              onClick={handleFlagSubmit}
              style={{ background: "#059669", color: "#fff", border: "none", padding: "0 24px", borderRadius: "6px", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}
            >
              ✓ Submit Flag
            </button>
          </div>
          <span style={{ fontSize: "11px", color: textSecondary, display: "block", marginTop: "8px" }}>
            Flags are case-sensitive. Expected format: <strong style={{ color: "#2563eb" }}>FLAG{'{ ... }'}</strong>
          </span>
        </div>

      </div>
      {showChallenge && (
        <ChallengePlatform 
          challengeType={
            (testDetails?.job_name?.toLowerCase().includes('full stack') || 
             testDetails?.description?.toLowerCase().includes('node.js') || 
             String(testDetails?.id) === '1') 
              ? 'fullstack' 
              : 'frontend'
          }
          onClose={() => setShowChallenge(false)}
          onSubmitFlag={(flag) => {
            setFlagInput(flag);
            setShowChallenge(false);
          }}
        />
      )}

      {showSuccessModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(6, 8, 20, 0.9)",
          backdropFilter: "blur(12px)",
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "sans-serif"
        }}>
          <div style={{
            background: "#0c0e17",
            border: "1px solid #10b981",
            boxShadow: "0 0 40px rgba(16, 185, 129, 0.25)",
            borderRadius: "16px",
            padding: "40px 32px",
            maxWidth: "480px",
            width: "90%",
            textAlign: "center",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px"
          }}>
            <div style={{
              width: "72px",
              height: "72px",
              borderRadius: "50%",
              background: "rgba(16, 185, 129, 0.15)",
              border: "2px solid #10b981",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "36px",
              color: "#10b981"
            }}>
              ✓
            </div>
            
            <div>
              <h2 style={{ fontSize: "22px", fontWeight: "700", margin: "0 0 10px 0", color: "#fff" }}>
                Challenge Successfully Completed! 🎉
              </h2>
              <p style={{ fontSize: "14px", color: "#94a3b8", lineHeight: "1.6", margin: 0 }}>
                Congratulations! You solved all tasks perfectly. Your challenge flag has been validated. 
                Your profile is updated with <strong style={{ color: "#f59e0b" }}>+500 XP</strong> and TechCorp Inc. has been notified!
              </p>
            </div>

            <div style={{
              width: "100%",
              height: "4px",
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "2px",
              overflow: "hidden",
              marginTop: "10px"
            }}>
              <div style={{
                height: "100%",
                background: "#10b981",
                width: "100%",
                animation: "shrink 3.5s linear forwards"
              }}></div>
            </div>
            
            <span style={{ fontSize: "11px", color: "#64748b" }}>
              Redirecting you to the Tests Listing page...
            </span>

            {/* Injected style keyframes for standard animations */}
            <style>{`
              @keyframes shrink {
                from { width: 100%; }
                to { width: 0%; }
              }
            `}</style>
          </div>
        </div>
      )}
    </div>
  );
}